import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";

import type { UserFeedbackRecord } from "../AgentThreadRecord";

/**
 * Fetch feedback the current user has submitted on ``threadId``.
 *
 * ``agentThreadOrgId`` is forwarded as ``X-Roboto-Org-Id`` so the backend's
 * ``REQUIRES_CALLER_ORG`` auth rule has an org to pin on, which is required
 * for callers who belong to more than one org.
 */
export function useAgentThreadFeedback(
  threadId: string | undefined,
  agentThreadOrgId?: string,
): ReturnType<typeof useQuery<UserFeedbackRecord[]>> {
  const { agentThreadService } = useDomainServices();

  return useQuery<UserFeedbackRecord[]>({
    // ``agentThreadOrgId`` is part of the request scope (sent as ``X-Roboto-Org-Id``)
    // so it must be in the cache key — two orgs viewing the same thread id
    // (admin browsing, or a multi-org user switching orgs) would otherwise
    // collide on a single cache entry and serve each other's responses.
    queryKey: ["agent-thread-feedback", threadId ?? "", agentThreadOrgId ?? ""],
    queryFn: ({ signal }) =>
      agentThreadService.getFeedbackForAgentThread(threadId as string, {
        abortSignal: signal,
        orgId: agentThreadOrgId,
      }),
    enabled: !!threadId,
    staleTime: 30_000,
  });
}

/**
 * Index of feedback records by ``message_sequence_num`` for O(1) per-message
 * lookups. The thread view renders one ``AgentThreadFeedback`` per assistant message
 * and previously did a linear scan inside each — N messages × N find() = O(N²)
 * per render. Building the map once per ``existingFeedback`` change collapses
 * that to O(N) total.
 */
export function useAgentThreadFeedbackBySequenceNum(
  threadId: string | undefined,
  agentThreadOrgId?: string,
): Map<number, UserFeedbackRecord> {
  const { data } = useAgentThreadFeedback(threadId, agentThreadOrgId);
  return React.useMemo(() => {
    const map = new Map<number, UserFeedbackRecord>();
    for (const record of data ?? []) {
      map.set(record.message_sequence_num, record);
    }
    return map;
  }, [data]);
}
